// Offline prewarm registry — the farm field-module LOOKUP queries that a
// device needs cached to fill its forms with no signal.
//
// "Prepare for offline" (src/components/OfflinePrepButton.tsx) runs every task
// here while ONLINE: each `fetchQuery` lands its result in the React Query
// cache under the SAME queryKey the page reads, and the PersistQueryClient
// dehydrates it to IndexedDB (24h maxAge). Offline, the page mounts, its own
// `refetchOnMount:'always'` refetch is PAUSED (networkMode 'online') and the
// pre-warmed cached rows are served — so the dropdowns aren't empty.
//
// IMPORTANT — these are deliberate MIRRORS of the per-page lookup queries. Each
// task's queryKey + result SHAPE must stay identical to its source page (noted
// in each `// mirror of …` comment); a mismatch just means a stale/absent
// offline dropdown (never a write/RLS problem — these are read-only). The real
// fix is to unify the duplicated block/worker/chemical lookups into shared
// fetchers (see REFACTOR-BACKLOG.md) and have BOTH the pages and this file use
// them; until then, keep the copies in sync when a page's lookup changes.
//
// ⚠️ AND THE "NEVER A WRITE/RLS PROBLEM" PROMISE ABOVE IS NOT UNIVERSAL. It
// holds for a dropdown. It does not hold for a task feeding a pay-crediting
// face match, where a stale empty array is indistinguishable to an operator
// from nobody being enrolled — see the piecework-face-vectors task, and #2216.
//
// `scripts/check-prewarm-mirror.mjs` now enforces the mirror rather than
// trusting it: a task must not reach a source its hook does not, and a task
// whose key no hook reads at all fails too. Both sides are derived from source
// on every run, so there is no list here to keep in step.

import type { QueryKey } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { SCOUT_TARGETS_ACTIVE_SELECT } from '@/hooks/useScoutTargets'
import { SPRAY_PLANTINGS_SELECT } from '@/hooks/useTenantPlantings'
import { PLANTING_FINISHED_STATUSES } from '@/lib/plantings'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { shapeQuickCounts } from '@/lib/quickCountTiles'
import { shapePieceworkDeviceProfile, NO_DEVICE } from '@/modules/shared/piecework/livenessPolicy'
import { EMBEDDING_VERSION } from '@/lib/faceRecognition'
import { rosterToCached } from '@/lib/faceVectorCodec'
import { HARVEST_CONFIG_SELECT } from '@/lib/useHarvestConfig'
import { sastTodayYMD } from '@/lib/dates'
import { mapBlockScope } from '@/hooks/useGrowerFarmBlockFilter'
import { endDatesFromRows } from '@/hooks/useCaptureEndDates'
import { FARM_MAP_BLOCK_COLUMNS } from '@/modules/farm/farmMapBlocks'
import { indexByKey, ownVisibleBlocks, type ReadingRow } from '@/lib/drainReadings'
import { fetchPhiSpraysByBlock, phiHarvestSpraysKey } from '@/modules/farm/phiHarvestSprays'
import { PPE_OWN_STOCK_SELECT, shapeIssuableItems } from '@/modules/farm/ppeData'

// ---------------------------------------------------------------------------
// Screen (code-chunk) warmers — the OTHER half of offline readiness.
//
// The farm routes are lazy-loaded chunks (App.tsx lazyWithReload). The SW
// precaches them at install, but a device whose precache was interrupted (the
// classic case: ?reset-sw=1 / fresh install, then offline before the ~5.5MB
// precache finished) hits "This section hasn't been saved for offline use yet"
// on first offline navigation — even though its DATA was prepped. Importing
// each module here loads it into the session's ES-module registry (works
// immediately, regardless of SW state) and pulls the chunk through the SW /
// HTTP cache for relaunches. Idempotent + ~free when already loaded.
//
// These specifiers MUST match App.tsx's lazy imports so Rollup resolves them
// to the same chunks. Sprays statically bundles SprayTasks + MySprayTasks +
// SprayRecords, so one import covers all spray flows. Map pages are left out
// deliberately — they'd drag the 1.7MB mapbox-gl chunk into memory on every
// prep, and map tiles need their own offline story anyway.
// ---------------------------------------------------------------------------
export interface ScreenWarmTask {
  id:    string
  label: string
  /** FM11 UF-07: the module keys whose screens this is. See unitsHeld. */
  modules: readonly string[]
  load:  () => Promise<unknown>
}

export const FARM_OFFLINE_SCREENS: ScreenWarmTask[] = [
  { id: 'screen-scouting',  label: 'Scouting screen', modules: ['scouting'],    load: () => import('@/modules/farm/Scouting') },
  { id: 'screen-picks',     label: 'Harvest screen', modules: ['harvest_picks'],     load: () => import('@/modules/farm/HarvestPicks') },
  { id: 'screen-sprays',    label: 'Sprays screen', modules: ['sprays'],      load: () => import('@/modules/farm/Sprays') },
  { id: 'screen-fuel',      label: 'Fuel screen', modules: ['farm_fuel'],        load: () => import('@/modules/farm/Fuel') },
  { id: 'screen-problems',  label: 'Problems screen', modules: ['farm_problems'], load: () => import('@/modules/farm/FarmProblems') },
  { id: 'screen-fert',      label: 'Fertiliser screen', modules: ['fert_applications'],  load: () => import('@/modules/farm/FertApplications') },
  { id: 'screen-equipment', label: 'Equipment screen', modules: ['farm_equipment'],   load: () => import('@/modules/farm/FarmEquipment') },
  { id: 'screen-phenology', label: 'Phenology screen', modules: ['phenology'],   load: () => import('@/modules/farm/Phenology') },
  // The wagon handshake is captured at the field edge and its own header says
  // "OFFLINE FIRST" — but neither its chunk nor its two reference reads were
  // ever on this list, so a cold-offline device met an unsendable form
  // (flow-B-07).
  { id: 'screen-loads',     label: 'Loads screen', modules: ['farm_loads'],       load: () => import('@/modules/farm/FarmLoads') },
  // FM05 UF-11: the drain round is walked in tunnels with no signal.
  { id: 'screen-drain',     label: 'Drain screen', modules: ['drain_monitoring'], load: () => import('@/modules/farm/DrainMonitoring') },
  // BigHarvest capture — same specifier App.tsx lazy-imports, so the chunk
  // the supervisor needs in the orchard is cached by the morning prep.
  { id: 'screen-harvest-capture', label: 'Harvest capture screen', modules: ['harvest_capture'], load: () => import('@/modules/harvest/HarvestCapture') },
  // FM07 UF-18: PPE is handed out in the orchard at season start, with no signal.
  { id: 'screen-ppe', label: 'PPE screen', modules: ['farm_ppe'], load: () => import('@/modules/farm/FarmPpe') },
]

export interface PrewarmTask {
  /** Stable id (for progress + de-dupe). */
  id:       string
  /** Human label shown in the prep UI. */
  label:    string
  /** FM11 UF-07: EVERY module whose screen reads this key. A lookup several
   *  screens share lists them all, so holding any one of them keeps it. A
   *  test pins that each unit names at least one real module key. */
  modules:  readonly string[]
  queryKey: QueryKey
  // Receives an AbortSignal so the prep can actually cancel a stuck request on
  // timeout (frees the connection on a marginal link); every supabase call
  // below forwards it via `.abortSignal(signal)`.
  queryFn:  (signal: AbortSignal) => Promise<unknown>
}

/** FM11 UF-07: the units this person can use — a unit stays when they hold
 *  ANY of its modules (hasModule, the rule the tiles and the route gate use).
 *  A scout's or worker's phone stops downloading spray, pick, fuel and
 *  BigHarvest units it cannot open. An undecided answer (null: the reads
 *  behind the gate are still loading) KEEPS the unit — preparing too much
 *  costs a request, preparing too little is an empty form in the orchard. */
export function unitsHeld<T extends { modules: readonly string[] }>(
  units: readonly T[],
  holds: (moduleKey: string) => boolean | null,
): T[] {
  return units.filter(u => u.modules.some(k => holds(k) !== false))
}

// Shared block fetch helper — every field module scopes the block dropdown to
// the active tenant's visible growers via the RPC (RLS permits visibility, it
// doesn't pick the active tenant). The select differs slightly per page, so we
// can't fully share the fn; the visible-set filter is the common part.
async function fetchVisibleGrowerIds(): Promise<Set<string>> {
  const { data, error } = await supabase.rpc('current_farm_owned_grower_ids')
  if (error) throw error
  return new Set<string>((data as string[] | null) ?? [])
}

/** A BigHarvest capture picker: the tenant's ACTIVE, undeleted rows of one
 *  master table, ordered by name — byte-identical to HarvestCapture.tsx's own
 *  query. A prewarm whose key or select has drifted fills a DIFFERENT cache
 *  entry and the dropdown is empty in the field anyway, which is the one
 *  failure this whole file exists to prevent.
 *  ⚠️ Tasks only now. Sites used to be the same query and are not any more
 *  (H1 D7): they also filter on their CLIENT, so they have their own task
 *  below. Routing sites back through here would warm a list that offers a
 *  removed client's sites again. */
function harvestPicker(
  id: string, label: string, table: 'harvest_tasks',
  select: string, ctx: { tenantId: string | null },
): PrewarmTask {
  return {
    id, label, modules: ['harvest_capture'],
    queryKey: [id, ctx.tenantId],
    queryFn: async (signal) => {
      if (!ctx.tenantId) return []
      const { data, error } = await supabase.from(table)
        .select(select)
        .eq('tenant_id', ctx.tenantId).eq('is_active', true).is('deleted_at', null)
        .order('name').abortSignal(signal)
      if (error) throw error
      return data ?? []
    },
  }
}

/** Tenant-scoped prewarm queryFn: the "no active tenant → empty, else run the
 *  builder with the tenant + abort signal, throw on error, rows or []" tail
 *  every tenant-keyed mirror shares. Keeps each entry down to its query. */
function tenantRows(
  ctx: { tenantId: string | null },
  build: (tenantId: string, signal: AbortSignal) => PromiseLike<{ data: any[] | null; error: { message: string } | null }>,
): (signal: AbortSignal) => Promise<any[]> {
  return async (signal) => {
    if (!ctx.tenantId) return []
    const { data, error } = await build(ctx.tenantId, signal)
    if (error) throw error
    return data ?? []
  }
}

/** The tenant's ACTIVE, undeleted own-roster workers, name-ordered — the
 *  worker_number + name picker shape SprayRecords and Fuel both read. Same
 *  contract as harvestPicker: one shape, one place to keep in step with the
 *  pages' ['<id>', tenantId] keys. */
function tenantWorkerPicker(id: string, label: string, ctx: { tenantId: string | null }): PrewarmTask {
  return {
    // Every screen whose picker reads ['spray-workers', tenantId] (useTenantWorkers).
    id, label, modules: ['sprays', 'farm_fuel', 'farm_equipment', 'fert_applications', 'phenology', 'scouting', 'farm_ppe', 'farm_logbook', 'farm_labour', 'labour', 'farm_compliance'],
    queryKey: [id, ctx.tenantId],
    // full_name: useTenantWorkers selects it and labels by it; a prepped
    // picker without it names people differently offline.
    queryFn: tenantRows(ctx, (tenantId, signal) => supabase.from('labour_workers')
      .select('worker_number, name, full_name')
      .eq('tenant_id', tenantId)
      .is('deleted_at', null).eq('is_active', true).order('name')
      .abortSignal(signal)),
  }
}

/** FM03 UF-02: a unit whose answer is MANY cache keys (one per block) writes
 *  them through this, so they take the prep's 24h life. Without it the unit
 *  does nothing — a caller with no query client to seed into. */
export type PrewarmSeed = (queryKey: QueryKey, data: unknown) => Promise<unknown>

export function buildFarmOfflineLookups(ctx: {
  tenantId: string | null; tenantKind?: string | null; seed?: PrewarmSeed
}): PrewarmTask[] {
  // Shared across the 5 block tasks. Each block lookup needs the same
  // visible-grower set; previously each fetched it independently, so a marginal
  // link was asked for it 5× per prep. Memoise it for this prep run.
  // Retry-aware: a rejection clears the memo so a later attempt re-fetches.
  // Deliberately NOT abort-bound — it's a tiny RPC, and letting it run to
  // completion means one block unit's timeout can't poison the set for the
  // other four (they just await the same in-flight promise).
  let growerIdsP: Promise<Set<string>> | null = null
  const sharedGrowerIds = (): Promise<Set<string>> => {
    if (!growerIdsP) {
      growerIdsP = fetchVisibleGrowerIds().catch(e => { growerIdsP = null; throw e })
    }
    return growerIdsP
  }

  // Common skeleton of the per-module block lookups below: name-ordered blocks
  // select, mapped to the PAGE's shape, narrowed to tenant-visible growers.
  // Each entry keeps only its variation points (select columns, whether the
  // page hides soft-deleted blocks, extra mapped fields) — the mirror contract
  // in the header still binds each entry to its page, not to this helper.
  const visibleBlocks = async (
    signal: AbortSignal,
    select: string,
    liveOnly: boolean,
    map: (r: any) => { grower_id: string },
  ): Promise<any[]> => {
    let q = supabase.from('blocks').select(select)
    if (liveOnly) q = q.is('deleted_at', null)
    const [visible, { data, error }] = await Promise.all([
      sharedGrowerIds(),
      q.order('block_name').abortSignal(signal),
    ])
    if (error) throw error
    return ((data ?? []) as any[]).map(map).filter(b => visible.has(b.grower_id))
  }
  return [
    // ---- Blocks (one per module — same data, page-specific shape) ----------
    {
      id: 'scouting-blocks', label: 'Scouting blocks',
      modules: ['scouting'],
      queryKey: ['scouting-blocks', ctx.tenantId],
      // mirror of Scouting.tsx ['scouting-blocks', tenantId] — M199 tenant-keyed
      // the page's query, and a prewarm mirror that stays on the bare key warms
      // something the page no longer reads: "Prepare for offline" reports success
      // and the orchard still gets an empty block picker (the M161 incident).
      // F019: this warmed a NARROWER row than the page reads under the same
      // key — no `commodity` (PhenologyQuickLog then printed "No stage
      // catalogue for this commodity" with the stages cache warm), no farm
      // fields, and liveOnly=false, so SOFT-DELETED blocks rode into the
      // picker and into the walk's block auto-pick. The page's own query is
      // three lines below in Scouting.tsx; this must match it.
      queryFn: (signal) => visibleBlocks(signal,
        `block_id, block_name, variety, commodity, boundary, boundary_override, puc_farm_id,
         puc_farms:puc_farm_id ( farm_name ),
         pucs:puc_id ( puc_code, grower_id, growers:grower_id ( name ) )`,
        true,
        (r) => ({ ...mapBlockScope(r), commodity: r.commodity ?? null, boundary: r.boundary_override ?? r.boundary })),
    },
    {
      // FM11 D3: Farm Problems' block picker and GPS block pick. Without this
      // the key was never prepared, so offline the picker was empty and the GPS
      // note said "no block boundaries are drawn yet" about a farm that has them.
      id: 'farm-problems-blocks', label: 'Problem blocks',
      modules: ['farm_problems'],
      queryKey: ['farm-problems-blocks', ctx.tenantId],
      // mirror of FarmProblems.tsx useFarmBlocks ['farm-problems-blocks', tenantId]:
      // the page's select (boundary + override, farm fields), live blocks only
      // (the page filters deleted_at), mapped through mapBlockScope.
      queryFn: (signal) => visibleBlocks(signal,
        `block_id, block_name, variety, boundary, boundary_override, puc_farm_id,
         puc_farms:puc_farm_id ( farm_name ),
         pucs:puc_id ( puc_code, grower_id, growers:grower_id ( name ) )`,
        true,
        (r) => ({ ...mapBlockScope(r), boundary: r.boundary_override ?? r.boundary })),
    },
    {
      // FM01 UF-16: the Farm Map's blocks, so its polygons and Find a block
      // work with no signal (FARM-1 promised it; the map said "No block
      // boundaries drawn yet" instead). Not a mirror — the SAME key builder
      // and the SAME fetcher the page's useFarmOwnedBlocks uses.
      id: 'farm-map-blocks', label: 'Farm map blocks',
      modules: ['farm_map'],
      // mirror of FarmMap.tsx useFarmOwnedBlocks(tenantId, FARM_MAP_BLOCK_COLUMNS):
      // the hook's key, its select (the one shared constant), and its read —
      // every page drained, narrowed to the farm's own growers.
      queryKey: ['farm-owned-blocks', ctx.tenantId, FARM_MAP_BLOCK_COLUMNS],
      queryFn: async (signal) => {
        const [visible, { rows, error }] = await Promise.all([
          sharedGrowerIds(),
          fetchAllPaged<{ pucs?: { grower_id?: string } | null }>((lo, hi) => supabase.from('blocks')
            .select(FARM_MAP_BLOCK_COLUMNS)
            .is('deleted_at', null)
            .order('block_name').order('block_id').range(lo, hi)
            .abortSignal(signal) as unknown as PromiseLike<{ data: { pucs?: { grower_id?: string } | null }[] | null; error: { message: string } | null }>),
        ])
        if (error) throw new Error(error)
        return (rows ?? []).filter(r => visible.has(r.pucs?.grower_id ?? ''))
      },
    },
    {
      id: 'harvest-blocks', label: 'Harvest blocks',
      modules: ['harvest_picks'],
      queryKey: ['harvest-blocks', ctx.tenantId],
      // mirror of HarvestPicks.tsx ['harvest-blocks', tenantId] — key AND shape:
      // the page maps through mapBlockScope (puc_farm_id/puc_farm_name feed the
      // grower→farm→block picker) and hides soft-deleted blocks (M73). A
      // narrower copy here served farm-less rows to the offline New-pick modal,
      // which emptied the picker's farm step in exactly the no-signal case this
      // prep exists for.
      queryFn: (signal) => visibleBlocks(signal,
        `block_id, block_name, variety, puc_farm_id,
         puc_farms:puc_farm_id ( farm_name ),
         pucs:puc_id ( puc_code, grower_id, growers:grower_id ( name ) )`,
        true,
        mapBlockScope),
    },
    {
      id: 'spray-blocks', label: 'Spray blocks',
      modules: ['sprays'],
      queryKey: ['spray-blocks', ctx.tenantId],
      // mirror of SprayRecords.tsx ['spray-blocks', pageTenantId] — a bare key
      // here warms a cache entry the tenant-keyed page never reads, so the
      // offline picker greys out empty despite a completed prep
      // F019 (same class): the page selects commodity + the farm fields and
      // filters deleted_at — its own comment calls that filter "the only
      // gate", because blocks_read_scoped has no deleted_at predicate. This
      // mirror warmed neither, so offline a REMOVED block was back in the
      // New-spray picker and its hectares fed targetHa, the per-hectare
      // dosing denominator.
      queryFn: (signal) => visibleBlocks(signal,
        `block_id, block_name, variety, commodity, hectares, puc_farm_id,
         puc_farms:puc_farm_id ( farm_name ),
         pucs:puc_id ( puc_code, grower_id, growers:grower_id ( name ) )`,
        true,
        (r) => ({ ...mapBlockScope(r), commodity: r.commodity ?? null, hectares: r.hectares })),
    },
    {
      id: 'spray-plantings', label: 'Spray plantings',
      modules: ['sprays', 'harvest_picks', 'fert_applications', 'scouting', 'phenology', 'farm_loads', 'farm_map', 'farm_compliance', 'farm_field_ops'],
      queryKey: ['spray-plantings', ctx.tenantId],
      // mirror of useTenantPlantings (src/hooks/useTenantPlantings.ts) — key
      // AND shape (SPRAY_PLANTINGS_SELECT + the FINISHED-status filter +
      // fetchAllPaged): the planting picker is the one lookup mig
      // 20261016222316 added to spray capture, and a cold-offline device
      // without it records whole-block sprays on half-planted pivots — the
      // exact mis-dosing the feature exists to fix.
      queryFn: async (signal) => {
        const { rows, error } = await fetchAllPaged<Record<string, unknown>>((lo, hi) =>
          supabase
            .from('farm_projects')
            .select(SPRAY_PLANTINGS_SELECT)
            .not('status', 'in', `(${PLANTING_FINISHED_STATUSES.join(',')})`)
            .is('deleted_at', null)
            .order('cycle_start_date', { ascending: false })
            .range(lo, hi)
            .abortSignal(signal) as unknown as PromiseLike<{ data: Record<string, unknown>[] | null; error: { message: string } | null }>)
        if (error) throw new Error(error)
        return rows
      },
    },
    {
      id: 'fert-blocks', label: 'Fertiliser blocks',
      modules: ['fert_applications'],
      queryKey: ['fert-blocks', ctx.tenantId],
      // mirror of FertApplications.tsx ['fert-blocks', tenantId] (M161)
      // F019 (same class): the page selects hectares + tree_count (the
      // fertiliser dosing denominators) and the farm fields; this warmed none
      // of them, so the offline capture form had no denominator to dose by.
      queryFn: (signal) => visibleBlocks(signal,
        `block_id, block_name, variety, hectares, tree_count, puc_farm_id,
         puc_farms:puc_farm_id ( farm_name ),
         pucs:puc_id ( puc_code, grower_id, growers:grower_id ( name ) )`,
        true,
        (r) => ({
          ...mapBlockScope(r),
          hectares:   r.hectares   != null ? Number(r.hectares)   : null,
          tree_count: r.tree_count != null ? Number(r.tree_count) : null,
        })),
    },
    {
      id: 'phenology-blocks', label: 'Phenology blocks',
      modules: ['phenology'],
      queryKey: ['phenology-blocks', ctx.tenantId],
      // mirror of Phenology.tsx ['phenology-blocks', tenantId] (block +
      // current-stage join) — M201-cluster tenant-keyed the page's query; a
      // bare-key prewarm here would warm an entry the page never reads.
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const [visible, blocksRes, currentRes] = await Promise.all([
          sharedGrowerIds(),
          // The farm columns feed the page's grower → farm → block picker; the
          // select AND the mapped row below must match Phenology.tsx, or a
          // prepped phone has blocks with no farm (check-prewarm-mirror: THIN).
          supabase.from('blocks')
            .select(`block_id, block_name, variety, commodity, hectares, puc_farm_id, boundary, boundary_override,
                     puc_farms:puc_farm_id ( farm_name ),
                     pucs:puc_id ( puc_code, grower_id, growers:grower_id ( name ) )`)
            .is('deleted_at', null)
            .abortSignal(signal),
          supabase.from('block_current_phenology')
            .select('block_id, stage_id, stage_name, stage_order, short_code, observed_at, days_since')
            .abortSignal(signal),
        ])
        if (blocksRes.error) throw blocksRes.error
        if (currentRes.error) throw currentRes.error
        const currMap = new Map<string, any>()
        for (const c of (currentRes.data ?? [])) currMap.set((c as any).block_id, c)
        return (blocksRes.data ?? []).map((r: any) => {
          const c = currMap.get(r.block_id) ?? {}
          return {
            block_id:    r.block_id,
            block_name:  r.block_name,
            variety:     r.variety,
            commodity:   r.commodity,
            hectares:    r.hectares,
            puc_code:    r.pucs?.puc_code  ?? '—',
            grower_id:   r.pucs?.grower_id ?? '',
            grower_name: r.pucs?.growers?.name ?? '—',
            puc_farm_id:   r.puc_farm_id,
            puc_farm_name: r.puc_farms?.farm_name ?? null,
            current_stage_id:    c.stage_id    ?? null,
            current_stage_name:  c.stage_name  ?? null,
            current_stage_order: c.stage_order ?? null,
            current_short_code:  c.short_code  ?? null,
            observed_at:         c.observed_at ?? null,
            days_since:          c.days_since  ?? null,
            // Log stage's Use GPS (FM06 UF-03): the drawn border rides the cache.
            boundary:            r.boundary_override ?? r.boundary ?? null,
          }
        }).filter((b: any) => visible.has(b.grower_id))
      },
    },

    // ---- Workers ------------------------------------------------------------
    {
      id: 'harvest-workers', label: 'Harvest workers',
      modules: ['harvest_picks'],
      queryKey: ['harvest-workers', ctx.tenantId],
      // mirror of HarvestPicks.tsx ['harvest-workers', tenantId] (explicit tenant scope)
      queryFn: async (signal) => {
        const rows = await tenantRows(ctx, (tenantId, sig) => supabase.from('labour_workers')
          .select('worker_number, name, worker_category')
          .eq('tenant_id', tenantId)
          .is('deleted_at', null).eq('is_active', true).order('name')
          .abortSignal(sig))(signal)
        return rows
          .filter((w: any) => w.worker_category == null || w.worker_category === 'FIELD')
          .map((w: any) => ({ worker_number: w.worker_number, name: w.name }))
      },
    },
    {
      id: 'seconded-workers-here', label: 'Seconded workers',
      modules: ['scouting', 'harvest_picks', 'packer_piecework', 'farm_labour', 'labour', 'farm_logbook', 'farm_ppe'],
      // M157: the hook's key is dated at the capture date; warm TODAY's key
      // (the common cold-offline case). A back-dated pick captured cold-
      // offline degrades to own-workers-only rather than offering a secondee
      // the save would refuse.
      queryKey: ['seconded-workers-here', ctx.tenantId, sastTodayYMD()],
      // mirror of useSecondedWorkersHere (shared-workers P2) — inbound
      // secondees must survive a cold-offline shift like the roster above.
      queryFn: async () => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.rpc('seconded_workers_here', { p_on: sastTodayYMD() })
        if (error) throw error
        return (data ?? []).map((r: any) => ({
          worker_number: r.worker_number,
          worker_name: r.worker_name,
          home_tenant_name: r.home_tenant_name,
          has_badge: r.has_badge,
        }))
      },
    },
    // mirror of SprayRecords.tsx ['spray-workers', tenantId] (explicit tenant scope)
    tenantWorkerPicker('spray-workers', 'Spray workers', ctx),
    // NO 'fuel-workers' TASK, and its absence is the point. Fuel.tsx's picker
    // moved to useAssignableWorkers — own roster ∪ seconded in — so it reads
    // ['spray-workers', tenantId] (the task above) and
    // ['seconded-workers-here', …], and nothing has read ['fuel-workers'] since.
    // The task outlived its hook and spent a round-trip every prep filling a
    // cache entry no screen serves. check-prewarm-mirror found it; the comment
    // that used to sit here described exactly that failure in the past tense
    // while it was still happening.

    // ---- Per-module reference data -----------------------------------------
    {
      id: 'farm-load-targets', label: 'Packhouse links',
      modules: ['farm_loads'],
      queryKey: ['farm-load-targets', ctx.tenantId],
      // mirror of FarmLoads.tsx ['farm-load-targets', tenantId]. Without this
      // the Send-a-load form cannot address a wagon offline: targets=[] made
      // validateLoadForm refuse the submit BEFORE insertOrQueueFarmLoad, so
      // the page's own offline queue was unreachable (flow-B-07).
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.rpc('my_farm_load_targets').abortSignal(signal)
        if (error) throw error
        return (data ?? [])
      },
    },
    {
      id: 'farm-loads-blocks', label: 'Load blocks',
      modules: ['farm_loads'],
      queryKey: ['farm-loads-blocks', ctx.tenantId],
      // mirror of FarmLoads.tsx ['farm-loads-blocks', tenantId] — the page's
      // own flat select (block_id + block_name, name-ordered, live only), NOT
      // the grower-scoped shape the five pickers above use.
      queryFn: tenantRows(ctx, (_tenantId, signal) => supabase.from('blocks')
        .select('block_id, block_name')
        .is('deleted_at', null).order('block_name')
        .abortSignal(signal)),
    },
    {
      id: 'scout-targets-active', label: 'Scout targets',
      modules: ['scouting', 'farm_traps'],
      queryKey: ['scout-targets-active', ctx.tenantId],
      // The select string is IMPORTED, not copied (scouting audit F020/F051):
      // this task's own copy had drifted from the capture forms', omitting
      // action_threshold + threshold_unit — so "Prepare for offline" produced
      // a cache in which the capture form's action-threshold cue and unit
      // default silently did not exist, on exactly the walk they were built
      // for. A prewarm that writes a NARROWER shape into a key the page reads
      // is worse than no prewarm: the page cannot tell the difference.
      // (M87: explicit tenant scope — the read RLS ORs is_platform_admin())
      queryFn: tenantRows(ctx, (tenantId, signal) => supabase.from('scout_targets')
        .select(SCOUT_TARGETS_ACTIVE_SELECT)
        .eq('tenant_id', tenantId)
        .is('deleted_at', null).eq('is_active', true).order('name')
        .abortSignal(signal)),
    },
    {
      id: 'chemicals-with-balance', label: 'Chemicals (spray)',
      modules: ['sprays'],
      queryKey: ['chemicals-with-balance', ctx.tenantId],
      // mirror of SprayRecords.tsx ['chemicals-with-balance', tenantId] (M200)
      queryFn: tenantRows(ctx, (tenantId, signal) => supabase.from('chemical_balances')
        .select('chemical_id, product_name, write_as, active_ingredient, spray_type, default_rate, default_unit, pre_harvest_interval_days, registration_number, notes, current_quantity, is_low_stock')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null).order('product_name')
        .abortSignal(signal)),
    },
    {
      id: 'chemical-balances-for-fert', label: 'Chemicals (fertiliser)',
      modules: ['fert_applications'],
      queryKey: ['chemical-balances-for-fert', ctx.tenantId],
      // mirror of FertApplications.tsx ['chemical-balances-for-fert', tenantId]
      // (M161). The tenant FILTER matches the page's read — without it this
      // prewarm stored a cross-tenant register under the tenant-keyed entry.
      queryFn: tenantRows(ctx, (tenantId, signal) => supabase.from('chemical_balances')
        // product_class is load-bearing: the page lists only FERTILISER-class
        // products, so rows without it filtered to an EMPTY picker after
        // "Prepare for offline" (SPRAY-FERT-UX-AUDIT SF-54).
        .select('chemical_id, product_name, product_class, spray_type, default_rate, default_unit, current_quantity, is_low_stock')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null).order('product_name')
        .abortSignal(signal)),
    },
    {
      id: 'spray-recipes-active', label: 'Spray recipes',
      modules: ['sprays'],
      queryKey: ['spray-recipes-active', ctx.tenantId],
      // mirror of SprayTasks.tsx ['spray-recipes-active', tenantId] — the page
      // key has been tenant-keyed for a while; a bare-key prewarm here warmed
      // an entry the modal never reads (M201-cluster).
      queryFn: tenantRows(ctx, (tenantId, signal) => supabase.from('spray_recipes')
        .select(`
          recipe_id, name, target, notes,
          spray_recipe_chemicals ( chemical_id, product_name_override, rate, unit,
            chem_amount, chem_unit, water_amount, water_unit, report_as, notes, position )
        `)
        .eq('tenant_id', tenantId)
        .is('deleted_at', null).eq('is_active', true).order('name')
        .abortSignal(signal)),
    },
    {
      id: 'farm-sprayers', label: 'Sprayers',
      modules: ['sprays'],
      queryKey: ['farm-sprayers', ctx.tenantId],
      // mirror of useFarmSprayers() ['farm-sprayers', tenantId] (M201)
      queryFn: tenantRows(ctx, (tenantId, signal) => supabase.from('farm_equipment')
        .select('equipment_id, name, reg_no, tank_capacity_litres, default_water_rate_lph, nozzle_count, pressure_bar')
        .eq('tenant_id', tenantId)
        .eq('equipment_type', 'SPRAYER').eq('status', 'ACTIVE')
        .is('deleted_at', null).order('name')
        .abortSignal(signal)),
    },
    {
      id: 'phenology-stages', label: 'Phenology stages',
      modules: ['phenology', 'scouting'],
      queryKey: ['phenology-stages', ctx.tenantId],
      // mirror of usePhenologyStages() ['phenology-stages', tenantId] (M98) —
      // key, tenant filter AND shape (incl. version) must match the hook's
      // read, or this prewarm warms an entry the pickers never touch.
      queryFn: tenantRows(ctx, (tenantId, signal) => supabase.from('phenology_stages')
        .select('stage_id, commodity, stage_name, stage_order, short_code, description, default_action, version')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null).eq('is_active', true)
        .order('commodity').order('stage_order')
        .abortSignal(signal)),
    },
    {
      id: 'fuel-tank-balances', label: 'Fuel tanks',
      modules: ['farm_fuel'],
      queryKey: ['fuel-tank-balances', ctx.tenantId],
      // mirror of Fuel.tsx ['fuel-tank-balances', tenantId] — the page gained the
      // tenant slot (and its .eq) in the H29 sweep and this task did not, so
      // "Prepare now" warmed a key nothing read and the Fuel page had no tanks
      // at a tank with no signal (audit MA-10).
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('fuel_tank_balances')
          .select('*').eq('tenant_id', ctx.tenantId).order('name')
          .abortSignal(signal)
        if (error) throw error
        return (data ?? [])
      },
    },
    {
      id: 'fuel-equipment', label: 'Fuel equipment',
      modules: ['farm_fuel'],
      queryKey: ['fuel-equipment', ctx.tenantId],
      // mirror of Fuel.tsx useFuelPickers ['fuel-equipment', tenantId] (MA-10)
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('farm_equipment')
          .select('equipment_id, name, equipment_type, reg_no, hours_counter, km_counter')
          .eq('tenant_id', ctx.tenantId)
          .is('deleted_at', null).eq('status', 'ACTIVE').order('name')
          .abortSignal(signal)
        if (error) throw error
        return (data ?? [])
      },
    },
    {
      id: 'farm-equipment', label: 'Equipment list',
      modules: ['farm_equipment'],
      queryKey: ['farm-equipment', ctx.tenantId],
      // mirror of FarmEquipment.tsx ['farm-equipment', tenantId] (MA-10)
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('farm_equipment')
          .select(`equipment_id, name, equipment_type, make, model, reg_no, year,
                   hours_counter, km_counter, purchase_date, purchase_cost_zar,
                   status, photo_url, notes, tank_capacity_litres,
                   default_water_rate_lph, nozzle_count, pressure_bar, version`)
          .eq('tenant_id', ctx.tenantId)
          .is('deleted_at', null).order('name')
          .abortSignal(signal)
        if (error) throw error
        return (data ?? [])
      },
    },
    {
      id: 'logbook-vehicles', label: 'Logbook vehicles',
      modules: ['farm_logbook'],
      queryKey: ['logbook-vehicles', ctx.tenantId],
      // mirror of VehicleLogbook.tsx useLogbookVehicles (FM08 UF-26): every
      // machine not deleted, status included (sold ones stay pickable for a
      // back-dated trip). The page orders them itself, so the raw rows go in.
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('farm_equipment')
          .select('equipment_id, name, reg_no, equipment_type, km_counter, status')
          .eq('tenant_id', ctx.tenantId)
          .is('deleted_at', null).order('name')
          .abortSignal(signal)
        if (error) throw error
        return (data ?? [])
      },
    },
    {
      id: 'field-ops-tractors', label: 'Field Ops tractors',
      modules: ['farm_field_ops'],
      queryKey: ['field-ops-tractors', ctx.tenantId],
      // mirror of FarmFieldOps.tsx ['field-ops-tractors', tenantId] (FM08 UF-26)
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('farm_equipment')
          .select('equipment_id, name, reg_no')
          .eq('tenant_id', ctx.tenantId)
          .eq('equipment_type', 'TRACTOR').eq('status', 'ACTIVE')
          .is('deleted_at', null).order('name')
          .abortSignal(signal)
        if (error) throw error
        return (data ?? [])
      },
    },
    {
      id: 'tractor-implements', label: 'Tractor implements',
      modules: ['farm_field_ops'],
      queryKey: ['tractor-implements', ctx.tenantId],
      // mirror of FarmFieldOps.tsx ['tractor-implements', tenantId] (FM08 UF-26)
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('tractor_implements')
          .select('tractor_implement_id, label, hrs_per_ha')
          .eq('tenant_id', ctx.tenantId)
          .eq('is_active', true).is('deleted_at', null).order('label')
          .abortSignal(signal)
        if (error) throw error
        return (data ?? [])
      },
    },
    {
      id: 'pick-teams', label: 'Piece-work teams',
      modules: ['harvest_picks'],
      queryKey: ['pick-teams', ctx.tenantId],
      // mirror of PieceworkCapture.tsx ['pick-teams', tenantId] — the key was
      // tenant-LESS here, so cold-offline TEAM_POOL capture read an empty
      // cache and the team picker was dead (W3 §4.1).
      queryFn: tenantRows(ctx, (tenantId, signal) => supabase.from('teams')
        .select('team_id, name').eq('tenant_id', tenantId)
        .is('deleted_at', null).eq('is_active', true).order('name')
        .abortSignal(signal)),
    },
    {
      id: 'pick-team-members', label: 'Team members',
      modules: ['harvest_picks'],
      queryKey: ['pick-team-members', ctx.tenantId],
      // mirror of PieceworkCapture.tsx ['pick-team-members', tenantId]
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('team_members')
          .select('team_id, worker_number').eq('tenant_id', ctx.tenantId).is('effective_to', null)
          .abortSignal(signal)
        if (error) throw error
        return (data ?? [])
      },
    },
    {
      id: 'farm-day-lends', label: 'Crew lends for today',
      modules: ['harvest_picks'],
      queryKey: ['farm-day-lends', ctx.tenantId, sastTodayYMD()],
      // mirror of useFarmDayLends.ts ['farm-day-lends', tenantId, day] (FM04
      // UF-09): who is lent to which crew today. Without it a cold-offline pool
      // capture shows only the permanent crew, and a lend made on another phone
      // this morning is invisible until the link returns.
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('farm_team_day_lends')
          .select('worker_number, team_id')
          .eq('tenant_id', ctx.tenantId).eq('work_date', sastTodayYMD()).is('deleted_at', null)
          .order('lent_at').limit(1000)
          .abortSignal(signal)
        if (error) throw error
        return (data ?? [])
      },
    },
    {
      id: 'piecework-face-vectors', label: 'Enrolled faces',
      modules: ['harvest_picks', 'packer_piecework'],
      queryKey: ['piecework-face-vectors', ctx.tenantId],
      // mirror of usePieceworkFaceVectors — and it MUST call the same RPC.
      //
      // ⚠️ DO NOT "RESTORE PARITY" BY PUTTING THE FLAT TENANT FILTER BACK.
      // This task previously ran
      //   .from('worker_face_embeddings').eq('tenant_id', ctx.tenantId)
      // which returns nothing for a host tenant whose workforce is SECONDED in
      // — faces are enrolled at the EMPLOYER. Because it writes into the key the
      // hook reads, that empty array became the CACHED, PERSISTED roster: prep
      // runs while online, fetchQuery lands [] under the key, PersistQueryClient
      // dehydrates it for 24h, and the screen mounts inside the hook's 10-minute
      // staleTime with networkMode 'offlineFirst' and serves it. So tapping
      // "Prepare for offline" — the intended workflow — silently reinstated the
      // exact defect mig 20261021030000 removes, and offline it did so
      // unconditionally, because the prep's value is the only value there is.
      //
      // ⚠️ AND THIS FILE'S HEADER PROMISE DOES NOT COVER THIS TASK. It says a
      // mirror mismatch is harmless — "a stale or absent offline dropdown, never
      // a write/RLS problem, these are read-only". True of the others. Not true
      // here: this feeds a pay-crediting face match, and a stale empty array is
      // indistinguishable to an operator from nobody being enrolled.
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase
          .rpc('piecework_face_roster', { p_embedding_version: EMBEDDING_VERSION })
          .abortSignal(signal)
        if (error) throw error
        // Same COMPACT cached shape as the hook (see faceVectorCodec.ts).
        return rosterToCached(data)
      },
    },
    {
      id: 'worker-badges', label: 'Worker badges',
      modules: ['harvest_picks', 'packer_piecework', 'harvest_capture', 'farm_labour', 'labour'],
      queryKey: ['worker-badges', ctx.tenantId],
      // mirror of useWorkerBadges.ts ['worker-badges', tenantId] — same select +
      // filters + row shape ({badge_code, worker_number}). Without this, a
      // scanned badge can't resolve to a worker cold-offline (resolveBadgeScan
      // returns null → "no badges synced") and capture silently falls back to
      // the roster pick — the exact gap the face-vector task above closes for
      // faces. The three piece-work identify paths must warm together.
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase
          .from('worker_badges')
          .select('badge_code, worker_number')
          .eq('tenant_id', ctx.tenantId)
          .is('revoked_at', null)
          .abortSignal(signal)
        if (error) throw error
        return data ?? []
      },
    },
    {
      id: 'piecework-device-profile', label: 'Piece-work device gate',
      modules: ['harvest_picks', 'packer_piecework'],
      queryKey: ['piecework-device-profile', ctx.tenantId],
      // mirror of faceScan.tsx usePieceworkDeviceProfile — the Face button's
      // gate AND the device's liveness mode, from one RPC, so both resolve from
      // cache offline instead of the button staying disabled. Same shape as the
      // hook's ({ allowed, liveness_mode }); the hook parses the mode.
      queryFn: async (signal) => {
        let token: string | null = null
        try { token = localStorage.getItem('kf49:clock-face:token') } catch { /* private mode */ }
        if (!token) return NO_DEVICE
        const { data, error } = await supabase.rpc('device_piecework_profile', { p_token: token }).abortSignal(signal)
        if (error) throw error
        return shapePieceworkDeviceProfile(data)   // the hook's shaper, so the cache holds ONE shape
      },
    },
    // Packhouse only: a farm tenant has no packhouse_config row, and a prep on
    // a marginal link must not spend a pool slot (and its retries) on it.
    ...(ctx.tenantKind === 'PACKHOUSE' ? [{
      id: 'packhouse-quick-counts', label: 'Piece-work quick-count tiles',
      modules: ['packer_piecework', 'config'],
      queryKey: ['packhouse-config', 'quick-counts', ctx.tenantId],
      // mirror of usePackhouseQuickCounts.ts — the packhouse's own tile ladder
      // for /packer-capture. Without it a cold-offline station falls back to
      // the default 1 / 5 / 10 / 25, which is not wrong but is not what the
      // packhouse chose.
      queryFn: async (signal) => {
        if (!ctx.tenantId) return {}
        const { data, error } = await supabase.from('packhouse_config')
          .select('piecework_quick_counts')
          .eq('tenant_id', ctx.tenantId)
          .abortSignal(signal)
          .maybeSingle()
        if (error) throw error
        return shapeQuickCounts(data?.piecework_quick_counts)
      },
    } satisfies PrewarmTask] : []),
    // ---- FM05 UF-11: the drain round, cold-offline ----------------------
    // mirror of DrainMonitoring.tsx fetchOwnBlocks ['drain-blocks', tenantId]
    // (own blocks, no packhouse child links, scoped to the farm's growers).
    {
      id: 'drain-blocks', label: 'Drain blocks',
      modules: ['drain_monitoring'],
      queryKey: ['drain-blocks', ctx.tenantId],
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const [{ data: visibleIds, error: vErr }, { data, error }] = await Promise.all([
          supabase.rpc('current_farm_owned_grower_ids').abortSignal(signal),
          supabase.from('blocks_resolved')
            .select('block_id, block_name, commodity, pucs:puc_id ( grower_id )')
            .is('deleted_at', null).is('master_block_id', null).order('block_name').abortSignal(signal),
        ])
        if (vErr)  throw vErr
        if (error) throw error
        return ownVisibleBlocks(visibleIds as string[] | null, data as unknown as Parameters<typeof ownVisibleBlocks>[1])
      },
    },
    // mirror of DrainMonitoring.tsx ['drain-stations', tenantId]
    {
      id: 'drain-stations', label: 'Drain stations',
      modules: ['drain_monitoring'],
      queryKey: ['drain-stations', ctx.tenantId],
      queryFn: tenantRows(ctx, (tenantId, signal) => supabase.from('drain_stations')
        .select('station_id, block_id, lane_label, variety, bag_count, sort_order, version')
        .eq('tenant_id', tenantId).is('deleted_at', null)
        .order('sort_order').order('lane_label').abortSignal(signal)),
    },
    // mirror of DrainMonitoring.tsx fetchDayReadings ['drain-readings', tenantId, today]:
    // the day's key is new each morning, so without it the first offline
    // open withheld capture ("today's readings couldn't load").
    {
      id: 'drain-readings-today', label: "Today's drain readings",
      modules: ['drain_monitoring'],
      queryKey: ['drain-readings', ctx.tenantId, sastTodayYMD()],
      queryFn: async (signal) => {
        if (!ctx.tenantId) return {}
        const { data, error } = await supabase.from('drain_measurements')
          .select('measurement_id, station_id, measured_date, in_ph, in_ec, in_volume_ml, in_temp_c, out_ph, out_ec, out_volume_ml, out_temp_c, no_drain, notes, runoff_pct, version')
          .eq('tenant_id', ctx.tenantId).eq('measured_date', sastTodayYMD()).is('deleted_at', null).abortSignal(signal)
        if (error) throw error
        return indexByKey((data ?? []) as ReadingRow[], r => r.station_id)
      },
    },
    // FM03 UF-02: today's spray-safety answer for every farm-owned block,
    // so the New pick chip and the map panel answer with no signal. One
    // farm-wide read (fetchPhiSpraysByBlock — the chip's own fetcher, so the
    // seeded rows are what it would fetch), split per block and written to
    // the chip's key through ctx.seed; ~4 requests instead of two per block.
    // prewarm-no-hook: its own key only marks the unit done (a re-tap skips
    // it); the data lands under PhiHarvestSignal's per-block keys.
    {
      id: 'phi-harvest-today', label: 'Spray safety today',
      modules: ['harvest_picks', 'farm_map'],
      queryKey: ['phi-harvest-prep', ctx.tenantId, sastTodayYMD()],
      queryFn: async (signal) => {
        const seed = ctx.seed
        if (!ctx.tenantId || !seed) return 0
        const today = sastTodayYMD()
        const [visible, { rows, error }] = await Promise.all([
          sharedGrowerIds(),
          fetchAllPaged<{ block_id: string; pucs?: { grower_id?: string } | null }>((lo, hi) => supabase.from('blocks')
            .select('block_id, pucs:puc_id ( grower_id )')
            .is('deleted_at', null)
            .order('block_id').range(lo, hi)
            .abortSignal(signal) as unknown as PromiseLike<{ data: { block_id: string; pucs?: { grower_id?: string } | null }[] | null; error: { message: string } | null }>),
        ])
        if (error) throw new Error(error)
        const ids = (rows ?? []).filter(r => visible.has(r.pucs?.grower_id ?? '')).map(r => r.block_id)
        const key0 = phiHarvestSpraysKey('', today)
        const byBlock = await fetchPhiSpraysByBlock(ids, key0[2], today, signal)
        await Promise.all(ids.map(id => seed(phiHarvestSpraysKey(id, today), byBlock.get(id) ?? [])))
        return ids.length
      },
    },
    {
      id: 'farm-config', label: 'Farm settings',
      modules: ['sprays', 'harvest_picks', 'scouting', 'phenology', 'fert_applications', 'farm_map', 'farm_weather', 'farm_estimates', 'farm_compliance', 'drain_monitoring', 'farm_logbook', 'farm_config'],
      queryKey: ['farm-config', ctx.tenantId],
      // mirror of useFarmConfig.ts ['farm-config', tenantId] (RAW row — matches FarmSettings cache)
      queryFn: async (signal) => {
        if (!ctx.tenantId) return null
        const { data, error } = await supabase.from('farm_config').select('*')
          .eq('tenant_id', ctx.tenantId).limit(1).abortSignal(signal).maybeSingle()
        if (error) throw error
        return (data ?? null)
      },
    },
    // ── BigHarvest capture (P4 residual) ────────────────────────────────
    // Each mirrors HarvestCapture.tsx's own query EXACTLY (key + select), so
    // the supervisor's morning prep fills the same cache the orchard screen
    // reads. Without these, a phone that leaves signal before opening
    // Capture has no roster and the day cannot start.
    {
      id: 'harvest-capture-sites', label: 'Harvest sites',
      modules: ['harvest_capture'],
      queryKey: ['harvest-capture-sites', ctx.tenantId],
      // mirror of useCaptureSites (src/modules/harvest/captureSites.ts) —
      // H1 D7: only sites of a CURRENT client (the `!inner` client embed + the
      // client's deleted_at); the client's name rides along so two clients'
      // same-name sites read apart (UF-10). captureSites.test.ts compares the
      // two chains call for call.
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('harvest_sites')
          .select('site_id, name, harvest_clients!inner(name, deleted_at)')
          .eq('tenant_id', ctx.tenantId).eq('is_active', true).is('deleted_at', null)
          .is('harvest_clients.deleted_at', null).order('name').abortSignal(signal)
        if (error) throw error
        return data ?? []
      },
    },
    // P4.7. Without this the task dropdown is empty in a dead block, and
    // general work cannot be started at all — the task is required.
    harvestPicker('harvest-capture-tasks', 'Harvest tasks', 'harvest_tasks', 'task_id, name', ctx),
    {
      id: 'harvest-capture-spans', label: 'Harvest teams',
      modules: ['harvest_capture'],
      queryKey: ['harvest-capture-spans', ctx.tenantId],
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('teams')
          .select('team_id, name, parent_team_id')
          .eq('tenant_id', ctx.tenantId).eq('worker_category', 'HARVEST')
          .eq('is_active', true).is('deleted_at', null).order('name').abortSignal(signal)
        if (error) throw error
        return data ?? []
      },
    },
    {
      id: 'harvest-capture-members', label: 'Harvest team rosters',
      modules: ['harvest_capture'],
      queryKey: ['harvest-capture-members', ctx.tenantId],
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('team_members')
          .select('team_id, worker_number')
          .eq('tenant_id', ctx.tenantId).is('effective_to', null).abortSignal(signal)
        if (error) throw error
        return data ?? []
      },
    },
    {
      id: 'harvest-capture-workers', label: 'Harvest pickers',
      modules: ['harvest_capture'],
      queryKey: ['harvest-capture-workers', ctx.tenantId],
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('labour_workers')
          .select('worker_number, name')
          .eq('tenant_id', ctx.tenantId).eq('worker_category', 'HARVEST')
          .eq('is_active', true).is('deleted_at', null).order('name').abortSignal(signal)
        if (error) throw error
        return data ?? []
      },
    },
    {
      id: 'harvest-capture-destinations', label: 'Harvest destinations',
      modules: ['harvest_capture'],
      queryKey: ['harvest-capture-destinations', ctx.tenantId],
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('harvest_destinations')
          .select('destination_id, name')
          .eq('tenant_id', ctx.tenantId).eq('is_active', true).is('deleted_at', null)
          .order('name').abortSignal(signal)
        if (error) throw error
        return data ?? []
      },
    },
    {
      id: 'harvest-capture-notes', label: 'Recent delivery notes',
      modules: ['harvest_capture'],
      queryKey: ['harvest-capture-notes', ctx.tenantId],
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        // The 14-day window is recomputed here rather than shared: prewarm and
        // render happen at different moments, and a window that slides by the
        // minutes between them is harmless (the echo catches a mistyped number
        // typed TODAY). Everything else — key, select, order, limit — must
        // mirror HarvestCapture's query exactly or the orchard screen misses
        // this cache and the guard dies in the dead-signal case it exists for.
        const since = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
        const { data, error } = await supabase.from('harvest_delivery_notes')
          .select('dn_id, dn_number, destination_id')
          .eq('tenant_id', ctx.tenantId).is('deleted_at', null)
          .gte('created_at', since).order('created_at', { ascending: false })
          .limit(200).abortSignal(signal)
        if (error) throw error
        return data ?? []
      },
    },
    {
      id: 'harvest-config', label: 'Harvest settings',
      modules: ['harvest_capture', 'harvest_dayend', 'harvest_config'],
      queryKey: ['harvest-config', ctx.tenantId],
      queryFn: async (signal) => {
        if (!ctx.tenantId) return null
        // Same select as useHarvestConfig — this warm shares its cache key, so
        // a narrower shape here would clobber what Capture reads.
        const { data, error } = await supabase.from('harvest_config')
          .select(HARVEST_CONFIG_SELECT)
          .eq('tenant_id', ctx.tenantId).abortSignal(signal).maybeSingle()
        if (error) throw error
        return data ?? null
      },
    },
    {
      id: 'commodities', label: 'Commodities',
      modules: ['farm_orchard'],
      queryKey: ['commodities'],
      // mirror of useCommodities.ts ['commodities'] — so Orchard's scoped block
      // picker (useEnabledCommodities) has the catalog offline.
      queryFn: async (signal) => {
        const { data, error } = await supabase.from('commodities')
          .select('*').eq('is_active', true).order('sort_order').abortSignal(signal)
        if (error) throw error
        return data
      },
    },
    {
      id: 'tenant-commodities', label: 'Enabled commodities',
      modules: ['farm_orchard'],
      queryKey: ['tenant-commodities', ctx.tenantId],
      // mirror of useEnabledCommodities ['tenant-commodities', tenantId] (keyed
      // and filtered on the tenant) — so the enabled set scopes the picker offline too.
      queryFn: async (signal) => {
        if (!ctx.tenantId) return []
        const { data, error } = await supabase.from('tenant_commodities')
          .select('commodity_id').eq('tenant_id', ctx.tenantId).abortSignal(signal)
        if (error) throw error
        return data ?? []
      },
    },
    // ---- Who has ended: the capture screens' leaver line ------------------
    // mirror of useCaptureEndDates ['capture-end-dates', tenantId] (the same
    // rows → object mapper, imported) — a crate captured offline for a worker past their
    // end date still shows the line (Jason, 5 Oct 2026; mig 20261037100000).
    {
      id: 'capture-end-dates', label: 'Workers past their end date',
      modules: ['harvest_picks', 'packer_piecework', 'harvest_capture'],
      queryKey: ['capture-end-dates', ctx.tenantId],
      queryFn: async (signal) => {
        if (!ctx.tenantId) return {}
        const { data, error } = await supabase.rpc('capture_worker_end_dates').abortSignal(signal)
        if (error) throw error
        return endDatesFromRows(data)
      },
    },
    // ---- FM07 UF-18: PPE issued with no signal -----------------------------
    // mirror of ppeData.ts useIssuableItems ['ppe-issuable-items', tenantId]:
    // the same select and the same shaper (imported, not copied), so the
    // Issue sheet's item list is the prepared one. The workers it also needs
    // are the 'spray-workers' + 'seconded-workers-here' tasks above (farm_ppe
    // is on both). A failed shared-store read keeps the farm's own items and
    // says the shared half is missing, exactly as the hook does.
    {
      id: 'ppe-issuable-items', label: 'PPE items',
      modules: ['farm_ppe'],
      queryKey: ['ppe-issuable-items', ctx.tenantId],
      queryFn: async (signal) => {
        if (!ctx.tenantId) return { items: [], sharedFailed: false }
        const own = await supabase.from('stock_balances')
          .select(PPE_OWN_STOCK_SELECT)
          .eq('tenant_id', ctx.tenantId).is('deleted_at', null).order('item').abortSignal(signal)
        if (own.error) throw own.error
        const shared = await supabase.rpc('get_shared_stock_balances').abortSignal(signal)
        return shapeIssuableItems(own.data ?? [], shared.data ?? [], !!shared.error)
      },
    },
  ]
}
